import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";
import { ENTITY_TYPES } from "../../other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { Grid } from "@bryntum/schedulerpro";
import { debounce } from "@/other/utils/otherUtils.jsx";

const CATEGORY_TO_NOTIFY_TYPE_MAP = {
  general: "isGeneralUpdates",
  notes: "isNotesUpdates",
  schedule: "isScheduleUpdates",
  checklists: "isChecklistUpdates",
  reminders: "isReminderUpdates",
  files: "isFilesUpdates",
  assignments: "assignedBoardUsers",
  incentives: "eligibleBoardUsers"
};

// Map notify types to their respective stores
const STORE_BY_NOTIFY_TYPE_KEY_MAP = {
  [CATEGORY_TO_NOTIFY_TYPE_MAP.general]: STORE_KEYS.notifyeeBoardUsers,
  [CATEGORY_TO_NOTIFY_TYPE_MAP.notes]: STORE_KEYS.notifyeeBoardUsers,
  [CATEGORY_TO_NOTIFY_TYPE_MAP.schedule]: STORE_KEYS.notifyeeBoardUsers,
  [CATEGORY_TO_NOTIFY_TYPE_MAP.checklists]: STORE_KEYS.notifyeeBoardUsers,
  [CATEGORY_TO_NOTIFY_TYPE_MAP.reminders]: STORE_KEYS.notifyeeBoardUsers,
  [CATEGORY_TO_NOTIFY_TYPE_MAP.files]: STORE_KEYS.notifyeeBoardUsers,
  [CATEGORY_TO_NOTIFY_TYPE_MAP.incentives]: STORE_KEYS.resourceStore,
  [CATEGORY_TO_NOTIFY_TYPE_MAP.assignments]: STORE_KEYS.resourceStore,
};

// Map entity types to their record type keys
const ENTITY_TYPE_TO_RECORD_KEY_MAP = {
  task: "taskId",
  resource: "resourceId",
  client: "clientId"
};

// Exclude types by entity (matching the logic from ownershipConstants.ts)
const EXCLUDE_NOTIFY_TYPE_BY_ENTITY_TYPE = {
  task: [CATEGORY_TO_NOTIFY_TYPE_MAP.assignments, CATEGORY_TO_NOTIFY_TYPE_MAP.incentives],
  client: [CATEGORY_TO_NOTIFY_TYPE_MAP.assignments, CATEGORY_TO_NOTIFY_TYPE_MAP.incentives],
  resource: [] // Resources support all notification types
};

export class SwivuUserOwnershipGrid extends Grid {
  static get $name() {
    return "SwivuUserOwnershipGrid";
  }

  static get type() {
    return WIDGET_KEYS.swivuUserOwnershipGrid;
  }

  static get configurable() {
    return {
      ...super.configurable,
      emptyText: "No ownership data found",
      cls: "swivu-modern-grid swivu-user-ownership-grid",
      headerHeight: 40,
      rowHeight: 42,
      style: "margin: auto;",
      maxWidth: "66rem",
      autoHeight: true,
      maxHeight: "36rem",
      features: {
        cellTooltip: false,
        cellMenu: false,
        stripe: false,
        sort: false,
        headerMenu: false,
      },
    };
  }

  construct(config) {
    config.columns = this.buildColumns();
    super.construct(...arguments);
    this.on("cellClick", this.handleCellClick, this);
    if (this.ownershipData && this.project) {
      this.store.data = this.transformToGridData();
    }
    this.swivuChanges = [];
    this.debouncedUpdateToBackend = debounce(this.updateBackendStore.bind(this), 1000);
  }

  buildColumns() {
    const notificationColumns = [
      { id: CATEGORY_TO_NOTIFY_TYPE_MAP.assignments, text: 'Assignments', field: CATEGORY_TO_NOTIFY_TYPE_MAP.assignments },
      { id: CATEGORY_TO_NOTIFY_TYPE_MAP.incentives, text: 'Points', field: CATEGORY_TO_NOTIFY_TYPE_MAP.incentives },
      { id: CATEGORY_TO_NOTIFY_TYPE_MAP.general, text: 'General', field: CATEGORY_TO_NOTIFY_TYPE_MAP.general },
      { id: CATEGORY_TO_NOTIFY_TYPE_MAP.notes, text: 'Notes', field: CATEGORY_TO_NOTIFY_TYPE_MAP.notes },
      { id: CATEGORY_TO_NOTIFY_TYPE_MAP.schedule, text: 'Schedule', field: CATEGORY_TO_NOTIFY_TYPE_MAP.schedule },
      { id: CATEGORY_TO_NOTIFY_TYPE_MAP.checklists, text: 'Checklists', field: CATEGORY_TO_NOTIFY_TYPE_MAP.checklists },
      { id: CATEGORY_TO_NOTIFY_TYPE_MAP.reminders, text: 'Reminders', field: CATEGORY_TO_NOTIFY_TYPE_MAP.reminders },
      { id: CATEGORY_TO_NOTIFY_TYPE_MAP.files, text: 'Files', field: CATEGORY_TO_NOTIFY_TYPE_MAP.files }
    ];

    return [
      {
        id: "entity",
        text: "Entity",
        field: "name",
        editor: false,
        width: 200,
        renderer: this.createEntityNameRenderer(),
        align: "left"
      },
      ...notificationColumns.map(col => ({
        type: 'check',
        id: col.id,
        text: col.text,
        field: col.field,
        width: 100,
        align: "center",
        renderer: ({ record, widgets }) => {
          // Hide checkbox if this notify type is excluded for this entity type
          const excludedTypes = EXCLUDE_NOTIFY_TYPE_BY_ENTITY_TYPE[record.entityType] || [];
          widgets[0].hidden = excludedTypes.includes(col.field);
        },
        listeners: {
          toggle: "up.onToggleCheckbox"
        }
      }))
    ];
  }


  onToggleCheckbox({ checked, record, source }) {
    const notifyType = source.field;

    // Skip if this notify type is excluded for this entity type
    const excludedTypes = EXCLUDE_NOTIFY_TYPE_BY_ENTITY_TYPE[record.entityType] || [];
    if (excludedTypes.includes(notifyType)) {
      return;
    }

    this.swivuChanges.push({
      record,
      notifyType,
      newValue: checked
    });
    this.debouncedUpdateToBackend();
  }

  transformToGridData() {
    if (!this.ownershipData || !this.project) return [];

    const allEntitiesMap = new Map();

    Object.entries(CATEGORY_TO_NOTIFY_TYPE_MAP).forEach(([categoryKey, notifyType]) => {
      const rawEntities = this.ownershipData[categoryKey] || [];
      rawEntities.forEach(({ entity_type, entity_id }) => {
        const entityKey = `${entity_type}-${entity_id}`;

        // Check if this notify type is excluded for this entity type
        const excludedTypes = EXCLUDE_NOTIFY_TYPE_BY_ENTITY_TYPE[entity_type] || [];
        const shouldInclude = !excludedTypes.includes(notifyType);

        if (!allEntitiesMap.has(entityKey)) {
          const entity = this.getEntityFromStore(entity_type, entity_id);
          if (entity) {
            const gridRow = {
              entityType: entity_type,
              entityId: entity_id,
              entityTypeKey: this.getEntityTypeKey(entity_type),
              name: this.formatEntityName(entity)
            };
            // Only set the notify type if it's not excluded
            if (shouldInclude) {
              gridRow[notifyType] = true;
            }
            allEntitiesMap.set(entityKey, gridRow);
          }
        } else if (shouldInclude) {
          allEntitiesMap.get(entityKey)[notifyType] = true;
        }
      });
    });

    return Array.from(allEntitiesMap.values());
  }

  getEntityFromStore(entityType, entityId) {
    if (!entityId || !this.project) return null;
    try {
      const storeMap = {
        task: this.project.extraTaskStore,
        resource: this.project.resourceStore,
        client: this.project.clientsStore
      };
      return storeMap[entityType]?.getById(entityId) || null;
    } catch (error) {
      console.warn(`Failed to get ${entityType} with id ${entityId}:`, error);
      return null;
    }
  }

  formatEntityName(entity) {
    return entity?.name || entity?.displayName || "Unnamed";
  }

  getEntityTypeKey(entityType) {
    const typeMap = {
      task: ENTITY_TYPES.TASK,
      resource: ENTITY_TYPES.RESOURCE,
      client: ENTITY_TYPES.CLIENT
    };
    return typeMap[entityType] || null;
  }

  getEntityBadge(entityType) {
    const badgeMap = {
      resource: 'R',
      client: 'R',
      task: 'T'
    };
    return badgeMap[entityType] || 'E';
  }

  getEntityBadgeColor(entityType) {
    const colorMap = {
      resource: 'bg-green-500/20 text-green-700 dark:bg-green-400/60 dark:text-green-100',
      client: 'bg-green-500/20 text-green-700 dark:bg-green-400/60 dark:text-green-100',
      task: 'bg-blue-500/20 text-blue-700 dark:bg-blue-400/60 dark:text-blue-100'
    };
    return colorMap[entityType] || 'bg-gray-500/20 text-gray-700 dark:bg-gray-400/60 dark:text-gray-100';
  }

  createEntityNameRenderer() {
    return ({ record }) => {
      const badge = this.getEntityBadge(record.entityType);
      const badgeColor = this.getEntityBadgeColor(record.entityType);

      return {
        tag: "div",
        class: "flex items-center gap-2 px-2",
        children: [
          {
            tag: "div",
            class: `w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${badgeColor}`,
            text: badge
          },
          {
            tag: "span",
            class: "text-sm font-normal text-foreground cursor-pointer hover:underline truncate",
            text: record.name,
            dataset: { entityType: record.entityTypeKey, entityId: String(record.entityId) },
            title: record.name
          }
        ]
      };
    };
  }

  handleCellClick({ event }) {
    const entityTarget = event.target.closest("[data-entity-type][data-entity-id]");
    if (entityTarget?.dataset.entityType && entityTarget?.dataset.entityId) {
      event.stopPropagation();
      this.navigateToEntity(entityTarget.dataset.entityType, entityTarget.dataset.entityId);
      return;
    }
  }

  navigateToEntity(entityType, entityId) {
    if (!entityType || !entityId || !this.entityManager) return;
    this.entityManager.openEntityManager({
      entityType,
      entityId: Number(entityId)
    });
  }

  updateBackendStore() {
    if (!this.project) {
      console.warn('Missing project for store update');
      return;
    }
    this.project.beginBatch();
    this.swivuChanges.forEach(change => {
      this.updateBackendStoreForChange(change);
    });
    this.swivuChanges = [];
    this.project.endBatch();
  }

  updateBackendStoreForChange({ record, notifyType, newValue }) {
    if (!this.project || !this.boardUserId) {
      console.warn('Missing project or boardUserId for store update');
      return;
    }
    const store = this.project.getCrudStore(STORE_BY_NOTIFY_TYPE_KEY_MAP[notifyType]);
    if (store.id === STORE_KEYS.resourceStore) {
      // For assignments and incentives, update the entity record directly
      this.updateResourceStoreEntity(record, notifyType, newValue);
    } else {
      // For other notify types, update notifyeeBoardUsers store
      this.updateNotifyeeBoardUsersStore(record, notifyType, newValue);
    }
  }

  updateResourceStoreEntity(record, notifyType, newValue) {
    const entity = this.getEntityFromStore(record.entityType, record.entityId);
    if (!entity) {
      console.warn(`Entity not found: ${record.entityType} ${record.entityId}`);
      return;
    }
    const currentBoardUserIds = entity[notifyType] || [];
    if (newValue) {
      if (!currentBoardUserIds.includes(this.boardUserId)) {
        entity[notifyType] = [...currentBoardUserIds, this.boardUserId];
      }
    } else {
      entity[notifyType] = currentBoardUserIds.filter(id => id !== this.boardUserId);
    }
  }

  updateNotifyeeBoardUsersStore(record, notifyType, newValue) {
    const store = this.project.getCrudStore(STORE_KEYS.notifyeeBoardUsers);
    const recordTypeKey = ENTITY_TYPE_TO_RECORD_KEY_MAP[record.entityType];
    if (!recordTypeKey) {
      console.warn(`Unknown entity type: ${record.entityType}`);
      return;
    }
    const notifyBoardUserRecord = store.find(r =>
      r.boardUserId === this.boardUserId &&
      r[recordTypeKey] === record.entityId
    );
    if (notifyBoardUserRecord) {
      // Update existing record
      notifyBoardUserRecord[notifyType] = newValue;
    } else if (newValue) {
      // Create new record if checking and doesn't exist
      const newRecord = {
        boardUserId: this.boardUserId,
        [recordTypeKey]: record.entityId,
        [notifyType]: true
      };
      store.add(store.createRecord(newRecord));
    }
  }

}

SwivuUserOwnershipGrid.initClass();
